import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useFilters, useLocales, useNotifications } from '../../../../hooks';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DynamicDateWithFallback, GetAllMediaQuery, MediaExportQuery, PaginationMeta } from '../../../../API';
import { omit } from 'lodash-es';
import { FormProvider, useForm } from 'react-hook-form';
import { withSelectedLayout } from '../../../../state/Layouts';
import Pagination from '../../../shared/Pagination';
import { useData } from '../../../../data-layer';
import { useLifecycle } from '../../../../hooks/General/useLifecycle';
import { toStartOfDayISO, toEndOfDayISO } from '../../../../utils/dateHelpers';
import ContentSearchResults from './ContentSearchResults';
import ContentSearchFilters, {
  ContentSearchWithFilters,
  GetAllMediaQueryParams,
  ContentSearchFilter,
  searchFilterParams,
  toDownloadableQueryParam
} from './ContentSearchFilters';
import ContentSearchSmartFilters, {
  ContentSearchSmartFiltersData,
  formDataToSmartQuery
} from './ContentSearchSmartFilters';
import { toBoolean } from '../../../../utils/textUtils';
import { ALL } from '../../../../utils/types/genericTypes';
import { contentSearchTestIds } from '../../../shared/TestsIds';
import { useRoute } from '../../../../hooks/General/useRoute';
import { createDefaultSearchParams } from '../../../../data-layer/DataManager';

interface IContentSearchProps {
  isContentPicker?: boolean;
  isForLayout?: boolean;
  onContentPicked?: (contentId: string) => void;
  useSmartQuery?: boolean; // Toggle between regular filters and smart query filters
}

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  searchResultsContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    borderTop: `1px solid ${theme.palette.divider}`
  },
  bottomSection: {
    background: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    padding: theme.spacing(3, 4)
  }
}));

const MAX_CSV_EXPORT_RESULTS = 10000;

function ContentSearch({
  isContentPicker,
  isForLayout,
  onContentPicked,
  useSmartQuery = false
}: IContentSearchProps): JSX.Element {
  const {
    media: {
      state: {
        withSearchParams,
        withSearchParamsScope,
        withSearchResultsIds,
        withContentGroups,
        withContentTypes,
        withContentTypesAllowed,
        withContentVertical,
        withRating
      },
      hook: { searchMedia, searchMediaBySmartQuery, exportMediaToCSV }
    }
  } = useData();

  const { classes } = useStyles();
  const { getFilter } = useFilters();
  const { willUnmount } = useLifecycle();
  const { notifyInfo, notifySuccess } = useNotifications();
  const { t } = useLocales();
  const { contentSearchScope, contentTypesAllowedForScope } = useRoute();

  // Lowercased to match the content type keys the search API works with
  const scopeContentTypes = useMemo(
    () => contentTypesAllowedForScope.map((contentType) => contentType.toLowerCase()),
    [contentTypesAllowedForScope]
  );

  const [contentTypes, setContentTypes] = useRecoilState(withContentTypes);
  const contentTypesAllowed = useRecoilValue(withContentTypesAllowed);
  const [contentGroups, setContentGroups] = useRecoilState(withContentGroups);
  const [contentVertical, setContentVertical] = useRecoilState(withContentVertical);
  const [rating, setRating] = useRecoilState(withRating);

  const [recoilSearchParams, setRecoilSearchParams] = useRecoilState(withSearchParams);
  const [searchParamsScope, setSearchParamsScope] = useRecoilState(withSearchParamsScope);

  const [isLoading, setIsLoading] = useState(false);
  const [queryData, setQueryData] = useState<GetAllMediaQuery>();
  const [smartQueryFormData, setSmartQueryFormData] = useState<ContentSearchSmartFiltersData>();
  const [mediaList, setMediaList] = useRecoilState(withSearchResultsIds);
  const selectedLayout = useRecoilValue(withSelectedLayout); // TODO: data-layer convert

  const formMethods = useForm<ContentSearchWithFilters | ContentSearchSmartFiltersData>();
  const { handleSubmit, reset, setValue, getValues } = formMethods;
  const [meta, setMeta] = useState<PaginationMeta>();

  const getContentTypes = async () => {
    const types = await getFilter('content', 'contentType');
    if (types) {
      let typesFiltered = types;
      if (contentTypesAllowed?.length > 0) {
        typesFiltered = types.filter((type) => contentTypesAllowed.includes(type.key.toUpperCase()));
      }
      setContentTypes(typesFiltered.map((type) => type.key));
    }
  };

  const getContentGroups = async () => {
    const types = await getFilter('content', 'contentGroupsDefault');
    if (types) setContentGroups(types.map((type) => type.key));
  };

  const getContentVertical = async () => {
    const types = await getFilter('content', 'contentVertical');
    if (types) setContentVertical(types.map((type) => type.key));
  };

  const getRating = async () => {
    const types = await getFilter('content', 'ratings.ratingValue');
    if (types) setRating(types.map((type) => type.key));
  };

  useEffect(() => {
    if (isForLayout) {
      const selectedLayoutCountries = selectedLayout?.countries;
      const countrySearchParams = { ...getValues(), country: selectedLayoutCountries };
      reset(countrySearchParams);
    }
  }, [selectedLayout]);

  useEffect(() => {
    getContentTypes();
  }, [contentTypesAllowed]);

  // Ensure contentType is always an array when resetting the form
  // Also normalize boolean filters to their enum string values
  const normalizeFilterValues = (params: typeof recoilSearchParams) => {
    // We need to cast through unknown because recoilSearchParams is a generic SearchParams
    // but we know it contains the fields from GetAllMediaQueryParams at runtime
    const paramsObj = params as unknown as Record<string, unknown>;
    const normalized: Record<string, unknown> = {
      ...paramsObj,
      contentType: Array.isArray(paramsObj.contentType)
        ? paramsObj.contentType
        : paramsObj.contentType
          ? [paramsObj.contentType as string]
          : []
    };

    // Normalize isPremium: convert boolean to enum string or remove if ALL
    if (normalized.isPremium !== undefined && normalized.isPremium !== 'ALL') {
      normalized.isPremium = normalized.isPremium === true || normalized.isPremium === 'true' ? 'true' : 'false';
    }

    // Normalize downloadable: legacy boolean/'true'/'false' → ALLOW/BLOCK; keep ALL
    if (normalized.downloadable !== undefined && normalized.downloadable !== ALL) {
      normalized.downloadable = toDownloadableQueryParam(normalized.downloadable as string | boolean) ?? ALL;
    }

    // Normalize orphaned: convert boolean to enum string or remove if ALL
    if (normalized.orphaned !== undefined && normalized.orphaned !== 'ALL') {
      normalized.orphaned = normalized.orphaned === true || normalized.orphaned === 'true' ? 'true' : 'false';
    }

    return normalized;
  };

  useEffect(() => {
    // Every section rendering this panel reads and writes the same persisted params, so filters left behind
    // by a different section have to be dropped instead of restored
    if (searchParamsScope !== contentSearchScope) {
      setSearchParamsScope(contentSearchScope);
      setRecoilSearchParams(createDefaultSearchParams());
      setMediaList([]);
      reset(createDefaultSearchParams() as GetAllMediaQueryParams);
      onSubmit();
    } else {
      const safeSearchParams = normalizeFilterValues(recoilSearchParams);
      reset(safeSearchParams as GetAllMediaQueryParams);
      // Only search if we don't already have results
      if (!mediaList.length) {
        onSubmit();
      }
    }
    if (!contentGroups.length) {
      getContentGroups();
    }
    if (!contentTypes.length) {
      getContentTypes();
    }
    if (!contentVertical.length) {
      getContentVertical();
    }
    if (!rating.length) {
      getRating();
    }
  }, []);

  const handleExport = async () => {
    setIsLoading(true);

    let query: MediaExportQuery;

    if (useSmartQuery && smartQueryFormData) {
      // Convert smart query form data to export query format
      // The backend's exportCsv accepts GetAllMediaQuery format
      const exportQuery: Partial<MediaExportQuery> = {
        limit: smartQueryFormData.limit,
        page: smartQueryFormData.page,
        // Include basic search fields that are supported by MediaExportQuery
        term: smartQueryFormData.keywords || smartQueryFormData.title,
        contentType: smartQueryFormData.contentType,
        categories: smartQueryFormData.genre ? [smartQueryFormData.genre] : undefined,
        country: smartQueryFormData.allowedCountry ? [smartQueryFormData.allowedCountry] : undefined,
        isPremium: smartQueryFormData.isPremium,
        contentVertical: smartQueryFormData.contentVertical ? [smartQueryFormData.contentVertical] : undefined,
        contentGroups: smartQueryFormData.contentGroups ? [smartQueryFormData.contentGroups] : undefined,
        downloadable: toDownloadableQueryParam(smartQueryFormData.downloadable),
        supplier: smartQueryFormData.supplier,
        yearReleased: smartQueryFormData.yearReleased,
        rating: smartQueryFormData.rating ? [smartQueryFormData.rating] : undefined
      };

      query = { ...exportQuery, maxResults: MAX_CSV_EXPORT_RESULTS } as MediaExportQuery;
    } else {
      // Use traditional query data
      query = { ...queryData, maxResults: MAX_CSV_EXPORT_RESULTS } as MediaExportQuery;
    }

    await exportMediaToCSV(query);
    setIsLoading(false);
    if (meta && meta.totalDocs <= MAX_CSV_EXPORT_RESULTS) {
      notifySuccess(t('downloaded_successfully'));
    } else {
      notifyInfo(t('epg.channels.downloaded_with_truncation'));
    }
  };

  const transformDateRangeFilters = (data: GetAllMediaQueryParams): GetAllMediaQuery => {
    const mapIsoDateInDynamicDate = (date?: DynamicDateWithFallback, mapCallback = toStartOfDayISO) => {
      return date?.isoDate || date?.dynamicDate
        ? {
            ...date,
            isoDate: date?.isoDate ? mapCallback(date.isoDate) : date?.isoDate
          }
        : undefined;
    };
    return omit(
      {
        ...data,
        publishWindowStartDateMinDynamic: mapIsoDateInDynamicDate(data.publishWindowStartDate?.startDate),
        publishWindowStartDateMaxDynamic: mapIsoDateInDynamicDate(data.publishWindowStartDate?.endDate, toEndOfDayISO),
        publishWindowEndDateMinDynamic: mapIsoDateInDynamicDate(data.publishWindowEndDate?.startDate),
        publishWindowEndDateMaxDynamic: mapIsoDateInDynamicDate(data.publishWindowEndDate?.endDate, toEndOfDayISO),
        ingestDateMinDynamic: mapIsoDateInDynamicDate(data.ingestDate?.startDate),
        ingestDateMaxDynamic: mapIsoDateInDynamicDate(data.ingestDate?.endDate, toEndOfDayISO)
      },
      ['publishWindowStartDate', 'publishWindowEndDate', 'ingestDate']
    );
  };

  const transformBooleanFilters = (data: GetAllMediaQueryParams): GetAllMediaQuery => {
    return {
      ...data,
      isPremium: toBoolean(data.isPremium as unknown as string),
      downloadable: toDownloadableQueryParam(data.downloadable),
      orphaned: toBoolean(data.orphaned as unknown as string)
    };
  };

  const transformCountryFilters = (data: GetAllMediaQueryParams): GetAllMediaQuery => ({
    ...data,
    country: data.country?.length ? data.country : undefined
  });

  // Filter out values that are not in the _filters array or are undefined
  const cleanupQueryData = (data: GetAllMediaQueryParams, filters?: ContentSearchFilter[]): GetAllMediaQueryParams => {
    const cleaned: GetAllMediaQueryParams = {};

    for (const [key, value] of Object.entries(data)) {
      // Always keep pagination and special fields
      if (key === 'page' || key === 'limit' || key === 'contentType') {
        if (value !== undefined && value !== null) {
          (cleaned as Record<string, unknown>)[key] = value;
        }
        continue;
      }

      // Check if this field is in the active filters
      const filterKey = (
        Object.entries(searchFilterParams) as Array<
          [ContentSearchFilter, (keyof GetAllMediaQueryParams | 'orphaned')[]]
        >
      ).find(([, fields]) => fields.includes(key as never))?.[0];

      // Only include this field if it's in the active filters list and has a value
      if (filterKey && filters?.includes(filterKey) && value !== undefined) {
        (cleaned as Record<string, unknown>)[key] = value;
      }
    }

    return cleaned;
  };

  const handleSearchSmart = async (data: ContentSearchSmartFiltersData) => {
    if (!willUnmount.current) setIsLoading(true);

    const { _filters, ...searchData } = data;
    // An empty content type filter must not widen the search past the types the section is allowed to show
    const queryFormData =
      data.contentType?.length || !scopeContentTypes.length ? data : { ...data, contentType: scopeContentTypes };
    const smartQuery = formDataToSmartQuery(queryFormData);

    // Store form data for export
    setSmartQueryFormData(queryFormData);

    if (!searchMediaBySmartQuery) {
      if (!willUnmount.current) setIsLoading(false);
      return;
    }

    const response = await searchMediaBySmartQuery(smartQuery);

    if (!willUnmount.current) setIsLoading(false);
    if (!response) {
      setMediaList([]);
      return;
    }

    if (!willUnmount.current) {
      if (!response.results) return;
      setMediaList(response.results.map((media) => media.contentId));
      const params = { ...searchData, limit: response.meta.limit, page: response.meta.page, _filters };
      setRecoilSearchParams(params);
      setMeta(response.meta);
      reset(params);
    }
  };

  const handleSearch = async (data: ContentSearchWithFilters) => {
    if (!willUnmount.current) setIsLoading(true);

    const { _filters, ...queryData } = data;
    const isContentTypeEmpty = queryData.contentType?.length === 0;
    if (isContentTypeEmpty) queryData.contentType = scopeContentTypes.length ? scopeContentTypes : contentTypes;

    // Clean up query data to only include fields that are in the active filters
    const cleanedQueryData = cleanupQueryData(queryData, _filters);

    const newQueryData = transformBooleanFilters(transformDateRangeFilters(transformCountryFilters(cleanedQueryData)));
    setQueryData(newQueryData);
    const response = await searchMedia(newQueryData);
    if (isContentTypeEmpty) queryData.contentType = [];

    if (!willUnmount.current) setIsLoading(false);
    if (!response) {
      setMediaList([]);
      return;
    }

    if (!willUnmount.current) {
      if (!response.results) return;
      setMediaList(response?.results?.map((media) => media.contentId));
      const params = { ...data, limit: response.meta.limit, page: response.meta.page, _filters };
      setRecoilSearchParams(params);
      setMeta(response.meta);
      reset(params);
    }
  };

  const handleNewSearch = (data: ContentSearchWithFilters | ContentSearchSmartFiltersData) => {
    const searchData = { ...data, page: 1 };
    if (useSmartQuery) {
      return handleSearchSmart(searchData as ContentSearchSmartFiltersData);
    }
    return handleSearch(searchData as ContentSearchWithFilters);
  };

  const onSubmit = () => {
    if (useSmartQuery) {
      handleSubmit((data) => handleSearchSmart(data as ContentSearchSmartFiltersData))();
    } else {
      handleSubmit((data) => handleSearch(data as ContentSearchWithFilters))();
    }
  };

  const onSubmitNew = (evt?: React.FormEvent<HTMLElement>) => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    handleSubmit(handleNewSearch)();
  };

  return (
    <FormProvider {...formMethods}>
      <div data-testid={contentSearchTestIds.root} className={classes.root}>
        {useSmartQuery ? (
          <ContentSearchSmartFilters
            mediaList={mediaList}
            paginationMeta={meta}
            onSubmit={onSubmitNew}
            onExport={handleExport}
            isLoading={isLoading}
            contentTypes={contentTypes}
            contentTypesAllowed={contentTypesAllowed}
          />
        ) : (
          <ContentSearchFilters
            mediaList={mediaList}
            paginationMeta={meta}
            onSubmit={onSubmitNew}
            onExport={handleExport}
          />
        )}
        <div className={classes.searchResultsContainer}>
          <ContentSearchResults
            isContentPicker={isContentPicker}
            onContentPicked={onContentPicked}
            isLoading={isLoading}
            mediaList={mediaList}
          />
          <div className={classes.bottomSection}>
            <Pagination
              queryMetaData={meta}
              onPageChange={(newPage: number) => {
                reset();
                setValue('page', newPage);
                onSubmit();
              }}
              onResultsChange={(newLimit: number) => {
                reset();
                setValue('page', 1);
                setValue('limit', newLimit);
                onSubmit();
              }}
            />
          </div>
        </div>
      </div>
    </FormProvider>
  );
}

export default ContentSearch;
